import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  Leaf,
  Calculator,
  TreePine,
  Recycle,
  Truck,
  ChevronDown
} from 'lucide-react';

// Constants moved outside component to prevent useEffect loop
const CONVERSION_FACTORS = {
  plasticSavedPerMat: 2.0, // lbs of plastic saved per mat
  co2SavedPerMat: 3.6      // lbs CO2 saved per mat
};

const Impact = () => {
  const [quantity, setQuantity] = useState(4);

  // Memoized results to prevent unnecessary recalculations
  const results = useMemo(() => {
    const plasticSaved = quantity * CONVERSION_FACTORS.plasticSavedPerMat;
    const co2Saved = quantity * CONVERSION_FACTORS.co2SavedPerMat;
    
    return {
      plasticSaved,
      co2Saved,
      matsUsed: quantity
    };
  }, [quantity]);

  const handleQuantityChange = (value) => {
    const numValue = parseInt(value) || 0;
    if (numValue >= 0) {
      setQuantity(numValue);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-automats-off-white to-white">
      {/* Enhanced Hero Section */}
      <section className="bg-white py-20 overflow-hidden relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 100 100" fill="none">
            <defs>
              <pattern id="impact-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#impact-grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <Badge className="bg-automats-eco-green/10 text-automats-eco-green border-automats-eco-green/20 px-6 py-2">
                <Leaf className="h-4 w-4 mr-2" />
                Environmental Impact
              </Badge>
              <div className="space-y-6">
                <h1 className="text-6xl lg:text-7xl font-heading font-bold text-automats-black leading-tight">
                  Eco Impact{' '}
                  <span className="text-automats-eco-green">Calculator</span>
                </h1>
                <p className="text-2xl font-body text-automats-steel leading-relaxed">
                  Discover the environmental benefits of choosing AutoMats 100% recycled floor mats. 
                  Calculate your positive impact on the planet with every purchase.
                </p>
              </div>
            </div>

            {/* Enhanced Calculator Preview */}
            <div className="bg-gradient-to-br from-automats-off-white to-white rounded-3xl p-12 shadow-2xl border border-gray-100">
              <h3 className="text-3xl font-heading font-bold mb-8 text-center text-automats-black">Environmental Benefits</h3>
              <div className="space-y-8">
                <div className="text-center">
                  <div className="text-6xl font-heading font-bold text-automats-eco-green mb-2">100%</div>
                  <div className="text-lg font-body text-automats-steel">Recycled Materials</div>
                  <div className="text-sm font-body text-automats-steel mt-1">Superior quality • Zero virgin content</div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-heading font-bold text-automats-red mb-2">2.0 lbs</div>
                    <div className="text-sm font-body text-automats-steel">Plastic Saved Per Mat</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-heading font-bold text-automats-steel mb-2">3.6 lbs</div>
                    <div className="text-sm font-body text-automats-steel">CO₂ Saved Per Mat</div>
                  </div>
                </div>
                <div className="text-center bg-automats-eco-green/10 p-4 rounded-xl">
                  <div className="text-lg font-heading font-semibold text-automats-black">Made in Dalton, GA</div>
                  <div className="text-sm font-body text-automats-steel">Reduced transport emissions</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Calculator Inputs */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center font-heading text-automats-black">
                <Calculator className="h-6 w-6 mr-2 text-automats-red" />
                Impact Calculator
              </CardTitle>
              <CardDescription className="font-body">
                Adjust the parameters below to calculate environmental impact
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-body font-medium text-automats-black">
                    Number of AutoMats Floor Mats
                  </Label>
                  <Input
                    type="number"
                    value={quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    className="mt-1 text-lg font-semibold"
                    min="0"
                    placeholder="Enter number of mats"
                  />
                  <p className="text-xs font-body text-automats-steel mt-1">
                    Each mat contains 100% superior-quality recycled materials
                  </p>
                </div>

                {/* Fixed values display */}
                <div className="bg-automats-off-white p-4 rounded-lg space-y-3">
                  <h4 className="font-body font-medium text-automats-black">AutoMats Specifications:</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-automats-steel">Recycled Content:</span>
                      <span className="font-semibold text-automats-eco-green">100%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-automats-steel">Plastic Saved/Mat:</span>
                      <span className="font-semibold text-automats-black">{CONVERSION_FACTORS.plasticSavedPerMat} lbs</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-automats-steel">CO₂ Saved/Mat:</span>
                      <span className="font-semibold text-automats-black">{CONVERSION_FACTORS.co2SavedPerMat} lbs</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-automats-steel">Origin:</span>
                      <span className="font-semibold text-automats-black">Dalton, GA</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="space-y-6">
            <Card className="bg-automats-off-white border-automats-eco-green border-2">
              <CardHeader>
                <CardTitle className="flex items-center font-heading text-automats-black">
                  <TreePine className="h-6 w-6 mr-2 text-automats-eco-green" />
                  Environmental Impact Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg text-center">
                    <div className="text-3xl font-heading font-bold text-automats-eco-green">
                      {results.matsUsed}
                    </div>
                    <div className="text-sm font-body text-automats-steel">AutoMats Floor Mats</div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg text-center">
                    <div className="text-3xl font-heading font-bold text-automats-eco-green">
                      {results.plasticSaved.toFixed(2)}
                    </div>
                    <div className="text-sm font-body text-automats-steel">lbs Plastic Waste Diverted</div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg text-center col-span-2">
                    <div className="text-4xl font-heading font-bold text-automats-eco-green">
                      {results.co2Saved.toFixed(2)}
                    </div>
                    <div className="text-sm font-body text-automats-steel">lbs CO₂ Equivalent Saved</div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border-l-4 border-automats-eco-green">
                  <h4 className="font-heading font-semibold text-automats-black mb-2">Your Environmental Impact</h4>
                  <p className="font-body text-automats-steel text-sm leading-relaxed">
                    By choosing <span className="font-semibold text-automats-eco-green">{results.matsUsed}</span> AutoMats 100% recycled floor mats, you're diverting{' '}
                    <span className="font-semibold text-automats-eco-green">{results.plasticSaved.toFixed(2)} lbs</span> of plastic waste from landfills
                    and preventing <span className="font-semibold text-automats-eco-green">{results.co2Saved.toFixed(2)} lbs CO₂e</span> from 
                    entering the atmosphere.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Real-world comparison */}
            <Card>
              <CardContent className="p-6">
                <h4 className="font-heading font-semibold text-automats-black mb-4 flex items-center">
                  <Recycle className="h-5 w-5 mr-2 text-automats-eco-green" />
                  Real-World Impact Comparison
                </h4>
                <div className="space-y-3 font-body text-sm">
                  <div className="flex justify-between items-center p-3 bg-automats-off-white rounded">
                    <span className="text-automats-steel">CO₂ saved equals:</span>
                    <span className="font-semibold text-automats-black">{(results.co2Saved / 4.6).toFixed(1)} gallons of gasoline not burned</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-automats-off-white rounded">
                    <span className="text-automats-steel">Plastic diverted equals:</span>
                    <span className="font-semibold text-automats-black">{Math.round(results.plasticSaved / 0.025)} plastic bottles recycled</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-automats-off-white rounded">
                    <span className="text-automats-steel">Environmental benefit:</span>
                    <span className="font-semibold text-automats-eco-green">100% recycled materials vs 0% typical</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-16">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="methodology">
              <AccordionTrigger className="font-heading text-lg text-automats-black hover:text-automats-red">
                <div className="flex items-center">
                  <Calculator className="h-5 w-5 mr-2" />
                  Calculation Methodology
                </div>
              </AccordionTrigger>
              <AccordionContent className="font-body text-automats-steel space-y-4">
                <div className="bg-green-50 border-l-4 border-automats-eco-green p-4 rounded">
                  <p className="text-sm text-green-800">
                    <strong>Verified Data:</strong> These calculations are based on AutoMats' verified product specifications 
                    and industry-standard environmental impact assessments.
                  </p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-semibold text-automats-black mb-2">Conversion Factors:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Plastic saved per mat: {CONVERSION_FACTORS.plasticSavedPerMat} lbs</li>
                      <li>• CO₂ equivalent saved per mat: {CONVERSION_FACTORS.co2SavedPerMat} lbs</li>
                      <li>• AutoMats recycled content: 100%</li>
                      <li>• Typical competitor recycled content: 0-20%</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h5 className="font-semibold text-automats-black mb-2">Environmental Benefits:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Diverts plastic waste from landfills</li>
                      <li>• Reduces demand for virgin plastic production</li>
                      <li>• Manufactured in Dalton, GA (reduced transport)</li>
                      <li>• Closes the loop on automotive carpet recycling</li>
                    </ul>
                  </div>
                </div>
                
                <p className="text-sm text-automats-steel italic">
                  AutoMats are made from 100% post-consumer recycled automotive carpeting, 
                  processed at our facility in Dalton, Georgia.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* CTA Section */}
        <div className="mt-16">
          <div className="bg-gradient-to-br from-automats-off-white to-white rounded-2xl p-12 shadow-xl border border-gray-100 text-center">
            <h3 className="text-4xl font-heading font-bold text-automats-black mb-6">
              Ready to Make a Real Impact?
            </h3>
            <p className="text-xl font-body text-automats-steel mb-8 max-w-3xl mx-auto leading-relaxed">
              Switch to AutoMats 100% recycled floor mats and start making a measurable 
              difference for the environment while improving your margins.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:sales@automats.us?subject=Pricing Request&body=Hello, I would like to request pricing information for AutoMats floor mats."
                className="bg-automats-red hover:bg-red-700 text-white px-8 py-4 rounded-xl font-body font-semibold transition-all duration-300 hover:scale-105 shadow-lg text-center"
              >
                Get Pricing
              </a>
              <a 
                href="mailto:sales@automats.us?subject=Sample Kit Request&body=Hello, I would like to request a sample kit for AutoMats floor mats."
                className="border-2 border-automats-red text-automats-red hover:bg-automats-red hover:text-white px-8 py-4 rounded-xl font-body font-semibold transition-all duration-300 hover:scale-105 text-center"
              >
                Request Sample Kit
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Impact;